import React, { useEffect, useState } from 'react';
import { Plus } from 'lucide-react';
import DefaultLayout from '../../../layout/DefaultLayout';
import BreadcrumbTwo from '../../../components/Breadcrumbs/BreadcrumbTwo';
import BasicDataTable from '../../../components/DataTable/BasicDataTable';
import ActivationModal from '../../../components/Modals/ActivationModal';
import ActionButton from '../../../components/Buttons/ActionButton';
import ProductGroupFormModal from '../../../components/Report/ProductGroups/ProductGroupFormModal';
import ProductReportGroupService from '../../../services/report/ProductReportGroupService';
import useAlerts from '../../../hooks/useAlerts';
import useApiErrorHandler from '../../../hooks/useApiErrorHandler';

interface GroupRow {
  id: number;
  name: string;
  status: boolean;
  productCount: number;
}

const ProductGroupsPage: React.FC = () => {
  const { show: showAlert } = useAlerts();
  const { handle } = useApiErrorHandler();

  const [groups, setGroups] = useState<GroupRow[]>([]);

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState<{
    id: number;
    name: string;
    products: { productId: number; productCode: string; productName: string }[];
  } | null>(null);

  const [isToggleOpen, setIsToggleOpen] = useState(false);
  const [toggleTarget, setToggleTarget] = useState<GroupRow | null>(null);

  const fetchGroups = async () => {
    try {
      const data = await ProductReportGroupService.getAll();
      setGroups(data ?? []);
    } catch (error: any) {
      handle(error);
    }
  };

  useEffect(() => {
    fetchGroups();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCreate = () => {
    setEditingGroup(null);
    setIsFormOpen(true);
  };

  const handleEdit = async (row: GroupRow) => {
    try {
      const detail = await ProductReportGroupService.getById(row.id);
      setEditingGroup({
        id: detail.id,
        name: detail.name,
        products: (detail.products ?? []).map((p: any) => ({
          productId: p.productId,
          productCode: p.productCode,
          productName: p.productName,
        })),
      });
      setIsFormOpen(true);
    } catch (error: any) {
      handle(error);
    }
  };

  const handleSave = async (data: { name: string; productIds: number[] }) => {
    try {
      const response = editingGroup
        ? await ProductReportGroupService.update(editingGroup.id, data)
        : await ProductReportGroupService.create(data);

      showAlert(response.type, response.title, response.message);
      setIsFormOpen(false);
      fetchGroups();
    } catch (error: any) {
      handle(error);
    }
  };

  const handleToggleRequest = (row: GroupRow) => {
    setToggleTarget(row);
    setIsToggleOpen(true);
  };

  const handleConfirmToggle = async () => {
    if (!toggleTarget) return;
    try {
      const response = await ProductReportGroupService.toggleStatus(toggleTarget.id);
      showAlert(response.type, response.title, response.message);
      fetchGroups();
    } catch (error: any) {
      handle(error);
    } finally {
      setIsToggleOpen(false);
      setToggleTarget(null);
    }
  };

  const columns = [
    { Header: 'Nombre', accessor: 'name' },
    { Header: 'Productos', accessor: 'productCount' },
    {
      Header: 'Estado',
      accessor: 'status',
      Cell: ({ value }: { value: boolean }) => (
        <span
          className={`inline-flex rounded-full px-3 py-1 text-sm font-medium ${
            value ? 'bg-green-500 text-white' : 'bg-red text-white'
          }`}
        >
          {value ? 'Activo' : 'Inactivo'}
        </span>
      ),
    },
  ];

  const actions = [
    { key: 'edit', onClick: handleEdit },
    { key: 'toggle', onClick: handleToggleRequest },
  ];

  const addButton = (
    <ActionButton
      label="Nuevo Grupo"
      icon={<Plus className="h-4 w-4" />}
      fullWidth={false}
      onClick={handleCreate}
    />
  );

  return (
    <DefaultLayout>
      <BreadcrumbTwo />

      <BasicDataTable
        columns={columns}
        data={groups}
        noDataMessage="No hay grupos de productos guardados."
        actions={actions}
        headerButton={addButton}
      />

      <ProductGroupFormModal
        isOpen={isFormOpen}
        onClose={() => setIsFormOpen(false)}
        onSave={handleSave}
        initial={editingGroup}
      />

      <ActivationModal
        isOpen={isToggleOpen}
        title={toggleTarget?.status ? '¿Desactivar Grupo?' : '¿Activar Grupo?'}
        message={
          toggleTarget?.status
            ? '¿Estás seguro de que deseas desactivar este grupo de productos?'
            : '¿Estás seguro de que deseas activar este grupo de productos?'
        }
        confirmText={toggleTarget?.status ? 'Desactivar' : 'Activar'}
        cancelText="Cancelar"
        onConfirm={handleConfirmToggle}
        onCancel={() => setIsToggleOpen(false)}
      />
    </DefaultLayout>
  );
};

export default ProductGroupsPage;
